import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { cn } from "@/lib/utils";
import { useCanvasId, getRelativePathFromCanvas } from "@/utils/utils";
import { Blocks } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { useDragImage } from "../utils";

export interface InstanceCardProps {
  instanceId: string;
  storyboardId: string;
  instanceFilepath?: string;
  screenshotUrl?: string | null;
  hasImageError?: boolean;
  onImageError?: () => void;
  width?: number;
  height?: number;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export const InstanceCard = React.memo(
  ({
    instanceId,
    storyboardId,
    instanceFilepath,
    screenshotUrl,
    hasImageError,
    onImageError,
    width,
    height,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onMouseLeave,
    onDragStart,
    onDragEnd,
  }: InstanceCardProps) => {
    const canvasId = useCanvasId();

    // Extract filename and relative path for display
    const { filename, relativePath } = useMemo(() => {
      if (!instanceFilepath || !canvasId)
        return { filename: "", relativePath: "" };
      const relPath =
        getRelativePathFromCanvas(instanceFilepath, canvasId) ||
        instanceFilepath;
      const name = relPath?.split("/").pop() || "";
      return { filename: name, relativePath: relPath || "" };
    }, [instanceFilepath, canvasId]);

    // Pre-cache image for drag (no zoom subscription - drag image created on demand)
    const { setupDragImage, cleanupDragImage } = useDragImage(
      screenshotUrl || undefined,
      width,
      height,
    );

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        // Read zoomPerc imperatively at drag time (no subscription)
        const zoomPerc =
          useCanvasStore.getState().canvasIdToPos[canvasId]?.zoomPerc || 1;
        setupDragImage(e, zoomPerc);
        onDragStart?.(e);
      },
      [canvasId, setupDragImage, onDragStart],
    );

    const handleDragEnd = useCallback(() => {
      cleanupDragImage();
      onDragEnd?.();
    }, [cleanupDragImage, onDragEnd]);

    return (
      <div
        className={cn(
          "p-2 rounded-lg cursor-pointer transition-all border w-full relative",
          "bg-secondary-n-950 hover:bg-secondary-n-700 border-border-primary hover:border-primary-0/50",
        )}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        draggable={!!onDragStart}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Screenshot Preview */}
        <div
          className="w-full aspect-square min-h-[40px] rounded-lg flex items-center justify-center"
          style={{
            background: "linear-gradient(90deg, #1F1F22 0%, #2F3035 100%)",
          }}
        >
          <div className="h-full w-full p-2 flex items-center justify-center overflow-hidden rounded-sm bg-secondary-n-50">
            {screenshotUrl && !hasImageError ? (
              <img
                src={screenshotUrl}
                alt={`Instance ${instanceId}`}
                className="object-scale-down rounded-sm max-w-full max-h-full"
                draggable={false}
                onError={onImageError}
              />
            ) : (
              <Blocks className="w-8 h-8 text-gray-400" />
            )}
          </div>
        </div>

        {/* Instance Filename (relative path on hover) */}
        <div className="flex items-center mt-2 min-h-[28px]">
          <span
            className="text-2xs font-medium text-secondary-n-50 truncate"
            title={relativePath}
          >
            {filename}
          </span>
        </div>
      </div>
    );
  },
);

InstanceCard.displayName = "InstanceCard";
